/**
 * Checks to see whether an element has entered view.
 * Requires intersection-observer polyfill for unsupported browsers - polyfill should be included in entry point
 */
export default class ScrollWatcher {
  private intersectionObserver!: IntersectionObserver;
  private scrollThreshold: number | null;

  /**
   * @param toWatch The element to watch
   * @param threshold padding inside of which the event is triggered
   */
  constructor(private toWatch: HTMLElement, threshold: number) {
    this.scrollThreshold = threshold;
  }

  /** Calls callback when the element to watch has entered. Stops observing after callback is called */
  public watch(callback: () => void): void {
    this.watchUsingIntersectionObserver(callback);
  }

  /** Stop watching */
  public stop() {
    if (!this.intersectionObserver) return;
    this.intersectionObserver.disconnect();
  }

  /**
   * When intersectionobserver is available, use it
   */
  private watchUsingIntersectionObserver(callback: () => void) {
    const intersectionOptions = this.scrollThreshold
      ? { rootMargin: `${this.scrollThreshold}px` }
      : undefined;

    this.intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            callback();
            observer.unobserve(entry.target);
          }
        });
      },
      intersectionOptions
    );
    this.intersectionObserver.observe(this.toWatch);
  }
}
